import { useLabInsightChat } from "~vue/composables/useLabInsightChat";
import { useLabInsightFeedback } from "~vue/composables/useLabInsightFeedback";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, onMounted } from "vue";

export type LabInsightLocation = "mywork" | "mywork_homepage_carousel" | "onboarding";

/**
 * Use `interactive: false` for cards without controls to skip the unused mount-time feedback request for
 * each reading.
 */
export function useLabInsightActions(insightId: string, location: LabInsightLocation = "mywork", { interactive = true }: { interactive?: boolean } = {}) {
    // Feedback
    const { fetchFeedback, feedbackFetching, feedbackData, feedbackSubmitting, submitFeedback, submitError } = useLabInsightFeedback(insightId);

    const feedbackValue = computed(() => feedbackData.value?.feedback?.value ?? null);
    const feedbackTags = computed(() => feedbackData.value?.feedback?.tags ?? []);
    const feedbackComment = computed(() => feedbackData.value?.feedback?.comment ?? null);

    async function handleFeedbackSelect(value: 0 | 1) {
        await submitFeedback({ insight_id: insightId, value, comment: null, tags: [] });
    }

    async function handleSubmitFeedback({ tags, comment, value }: { tags: string[]; comment: string | null; value: 0 | 1 }) {
        // Submit through the full feedback endpoint (rather than the tags-only endpoint) so the
        // comment persists. The rating comes from the component's pending value (not the fetched
        // computed, which may not have updated yet) so the submitted value matches what the user
        // clicked; since it equals the current rating the backend does not treat this as a value
        // change (which would clear tags).
        await submitFeedback({ insight_id: insightId, value, comment, tags });
    }

    onMounted(async () => {
        if (interactive) {
            await fetchFeedback();
        }
    });

    const { isStartingChat, chatError, handleStartChat: startLabInsightChat } = useLabInsightChat(insightId);

    async function handleStartChat() {
        if (isStartingChat.value) {
            return null;
        }
        // Preserve the existing My Work/homepage event while the New Chat prompt uses
        // useLabInsightChat directly with its own enum-backed analytics.
        void logUserInteraction(AmplitudeEventNames.INSIGHT_DIVE_DEEPER_CLICKED, { insightId, location });
        return startLabInsightChat();
    }

    return {
        // Feedback
        feedbackValue,
        feedbackTags,
        feedbackComment,
        feedbackData,
        feedbackFetching,
        feedbackSubmitting,
        submitError,
        handleFeedbackSelect,
        handleSubmitFeedback,

        // Chat
        isStartingChat,
        chatError,
        handleStartChat,
    };
}
